import { useState, FormEvent, useEffect } from 'react';
import { SelectedItem } from '../page';

const KAKAO_LINK = 'https://open.kakao.com/o/s3bl7ioi';

interface ContactFormProps {
  selectedItems: SelectedItem[];
  onRemoveItem: (id: string) => void;
  onUpdateQuantity: (id: string, delta: number) => void;
  onClearItems: () => void;
}

const ContactForm = ({ selectedItems, onRemoveItem, onUpdateQuantity, onClearItems }: ContactFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', address: '', memo: '' });

  const totalPrice = selectedItems.reduce((sum, item) => {
    const num = parseInt(item.price.replace(/[^0-9]/g, ''), 10);
    return sum + (isNaN(num) ? 0 : num * item.quantity);
  }, 0);

  const formatPrice = (n: number) => n.toLocaleString('ko-KR') + '원';

  const buildOrderText = (data: typeof formData, items: SelectedItem[]) => {
    const itemLines = items.map((it) => {
      const unitPrice = parseInt(it.price.replace(/[^0-9]/g, ''), 10);
      const lineTotal = isNaN(unitPrice) ? it.price : formatPrice(unitPrice * it.quantity);
      return `- ${it.fruitName} ${it.weight} x${it.quantity}개 = ${lineTotal}`;
    }).join('\n');
    const itemsText = items.length > 0 ? `\n[주문 상품]\n${itemLines}\n\n총 주문 금액: ${formatPrice(totalPrice)}` : '';
    const memoText = data.memo ? `\n\n[요청사항]\n${data.memo}` : '';
    const bankText = `\n\n[입금 계좌]\nK뱅크 100-230-277985 (김성훈)`;
    return `[주문 정보]\n이름: ${data.name}\n전화번호: ${data.phone}\n주소: ${data.address}${itemsText}${memoText}${bankText}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedItems.length === 0) return;
    const text = buildOrderText(formData, selectedItems);
    navigator.clipboard.writeText(text).then(() => setCopied(true)).catch(() => setCopied(false));
    window.open(KAKAO_LINK, '_blank');
    setSubmitted(true);
  };

  const handleReopenKakao = () => {
    const text = buildOrderText(formData, selectedItems);
    navigator.clipboard.writeText(text).then(() => setCopied(true));
    window.open(KAKAO_LINK, '_blank');
  };

  const handleReset = () => {
    setSubmitted(false);
    setCopied(false);
    setFormData({ name: '', phone: '', address: '', memo: '' });
    onClearItems();
  };

  useEffect(() => {
    if (submitted && selectedItems.length === 0) {
      setSubmitted(false);
    }
  }, [selectedItems, submitted]);

  return (
    <section id="contact" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* 상단 헤더 배너 - 모바일 대응 */}
        <div className="relative rounded-3xl overflow-hidden mb-10">
          <img
            src="https://public.readdy.ai/ai/img_res/e3e02f1fd59a02ed7b99bfece8e7c300.jpg"
            alt="과일 이미지"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/55"></div>
          <div className="relative z-10 px-6 py-8 md:px-14 md:py-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-green-300 mb-2 block">ORDER</span>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-extrabold text-white leading-snug mb-4">
              신선한 과일을 지금 바로{' '}
              <span className="text-green-400">주문하세요</span>
            </h2>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-5">
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <i className="ri-kakao-talk-fill text-yellow-400 flex-shrink-0"></i>
                <span>카카오톡으로 간편 주문</span>
              </div>
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <i className="ri-truck-line text-green-400 flex-shrink-0"></i>
                <span>당일 수확 · 신선 배송</span>
              </div>
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <i className="ri-time-line text-green-400 flex-shrink-0"></i>
                <span>주문 접수 후 빠른 답변</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

          {/* 왼쪽: 주문 폼 */}
          <div className="bg-gray-50 rounded-3xl p-6 md:p-8">
            <h3 className="text-xl font-extrabold text-gray-900 mb-1">고객 주문</h3>
            <p className="text-gray-500 text-sm mb-6">
              위 상품 목록에서 원하는 가격을 클릭하면 아래에 자동으로 담깁니다.
            </p>

            {submitted ? (
              <div className="flex flex-col items-center py-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-yellow-100 rounded-full mb-3">
                  <i className="ri-kakao-talk-fill text-yellow-500 text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">카카오톡이 열렸습니다!</h4>
                <div className={`flex items-center gap-2 mt-2 mb-5 px-4 py-2 rounded-full text-sm font-semibold ${copied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  <i className={copied ? 'ri-check-double-line' : 'ri-file-copy-line'}></i>
                  {copied ? '주문 정보가 복사되었습니다!' : '복사 중...'}
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 text-left w-full mb-5">
                  <p className="text-xs font-bold text-yellow-700 mb-2">
                    <i className="ri-information-line mr-1"></i>
                    카카오톡 채팅창에 붙여넣기 후 전송하세요
                  </p>
                  <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed bg-white rounded-xl px-3 py-2.5 border border-yellow-100">
                    {buildOrderText(formData, selectedItems)}
                  </p>
                </div>
                <div className="flex flex-col gap-3 w-full">
                  <button onClick={handleReopenKakao} className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-kakao-talk-fill text-lg"></i>
                    카카오톡 다시 열기 + 재복사
                  </button>
                  <button onClick={handleReset} className="w-full border border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700 font-medium py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">
                    새 주문 작성하기
                  </button>
                </div>
              </div>
            ) : (
              <form id="order-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">

                {/* 선택된 상품 목록 */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    선택한 상품
                    {selectedItems.length > 0 && (
                      <span className="ml-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {selectedItems.length}개
                      </span>
                    )}
                  </label>

                  {selectedItems.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-5 text-center">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-2">
                        <i className="ri-shopping-basket-line text-gray-400 text-lg"></i>
                      </div>
                      <p className="text-sm text-gray-400 font-medium">아직 선택한 상품이 없어요</p>
                      <p className="text-xs text-gray-400 mt-1">위 상품 목록에서 가격을 눌러주세요</p>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                      {selectedItems.map((item, idx) => {
                        const unitPrice = parseInt(item.price.replace(/[^0-9]/g, ''), 10);
                        const lineTotal = isNaN(unitPrice) ? item.price : formatPrice(unitPrice * item.quantity);
                        return (
                          <div
                            key={item.id}
                            className={`flex flex-col px-3 py-3 gap-2 ${idx !== selectedItems.length - 1 ? 'border-b border-gray-100' : ''}`}
                          >
                            {/* 상품명 + 삭제 */}
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 leading-snug break-keep">{item.fruitName}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{item.weight}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => onRemoveItem(item.id)}
                                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-400 cursor-pointer transition-colors flex-shrink-0 mt-0.5"
                              >
                                <i className="ri-close-line text-sm"></i>
                              </button>
                            </div>
                            {/* 수량 조절 + 금액 */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => onUpdateQuantity(item.id, -1)}
                                  className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-red-100 hover:text-red-500 rounded-full cursor-pointer transition-colors text-gray-500"
                                >
                                  <i className="ri-subtract-line text-xs"></i>
                                </button>
                                <span className="text-sm font-bold text-gray-900 w-6 text-center">{item.quantity}</span>
                                <button
                                  type="button"
                                  onClick={() => onUpdateQuantity(item.id, 1)}
                                  className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-green-100 hover:text-green-600 rounded-full cursor-pointer transition-colors text-gray-500"
                                >
                                  <i className="ri-add-line text-xs"></i>
                                </button>
                              </div>
                              <span className="text-sm font-extrabold text-green-600 whitespace-nowrap">{lineTotal}</span>
                            </div>
                          </div>
                        );
                      })}
                      <div className="px-4 py-2 bg-gray-50 flex items-center justify-between border-t border-gray-100">
                        <span className="text-xs text-gray-500">총 {selectedItems.length}가지 상품</span>
                        <button
                          type="button"
                          onClick={onClearItems}
                          className="text-xs text-red-400 hover:text-red-600 cursor-pointer transition-colors"
                        >
                          전체 삭제
                        </button>
                      </div>
                      {/* 총 금액 */}
                      <div className="px-4 py-3.5 bg-green-500 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <i className="ri-price-tag-3-fill text-white text-base"></i>
                          <span className="text-sm font-bold text-white">총 주문 금액</span>
                        </div>
                        <span className="text-xl font-extrabold text-white">{formatPrice(totalPrice)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">이름 <span className="text-red-500">*</span></label>
                    <input type="text" name="name" required value={formData.name} onChange={handleChange} placeholder="홍길동"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-green-400 bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">전화번호 <span className="text-red-500">*</span></label>
                    <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} placeholder="010-0000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-green-400 bg-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">배송 주소 <span className="text-red-500">*</span></label>
                  <input type="text" name="address" required value={formData.address} onChange={handleChange} placeholder="서울시 강남구 테헤란로 123"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-green-400 bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    요청사항 <span className="text-gray-400 font-normal">(선택)</span>
                  </label>
                  <textarea name="memo" rows={3} maxLength={500} value={formData.memo} onChange={handleChange}
                    placeholder="추가 요청사항이 있으시면 입력해주세요."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-green-400 bg-white resize-none"></textarea>
                </div>

                <button
                  type="submit"
                  disabled={selectedItems.length === 0}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-gray-900 font-semibold py-3.5 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-kakao-talk-fill text-lg"></i>
                  {selectedItems.length === 0 ? '상품을 먼저 선택해주세요' : '주문 정보 복사 + 카카오톡 열기'}
                </button>
              </form>
            )}
          </div>

          {/* 오른쪽: 단계별 사용 안내 */}
          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 md:p-8">
            <p className="text-sm font-extrabold text-amber-700 mb-6 flex items-center gap-2">
              <i className="ri-lightbulb-line text-base"></i>
              이렇게 하시면 됩니다!
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-4 border border-amber-100 flex flex-col gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-yellow-400 rounded-full text-white font-extrabold text-sm flex-shrink-0">1</div>
                <p className="text-sm font-bold text-gray-800">가격을 눌러 담기</p>
                <p className="text-xs text-gray-500 leading-relaxed">상품 카드를 클릭하고 원하는 무게/가격 버튼을 누르면 주문에 담깁니다</p>
                <div className="mt-auto pt-2 flex items-center gap-1.5 bg-green-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full justify-center">
                  <i className="ri-check-line"></i>
                  <span>가격 선택 완료</span>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-amber-100 flex flex-col gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-yellow-400 rounded-full text-white font-extrabold text-sm flex-shrink-0">2</div>
                <p className="text-sm font-bold text-gray-800">노란 버튼을 누르세요</p>
                <p className="text-xs text-gray-500 leading-relaxed">이름·전화번호·주소 입력 후 노란 버튼을 누르면 주문 내용이 자동 복사됩니다</p>
                <div className="mt-auto pt-2 flex items-center gap-1.5 bg-yellow-400 text-gray-900 text-xs font-semibold px-3 py-1.5 rounded-full justify-center">
                  <i className="ri-kakao-talk-fill"></i>
                  <span>카카오톡 열기</span>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-amber-100 flex flex-col gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-yellow-400 rounded-full text-white font-extrabold text-sm flex-shrink-0">3</div>
                <p className="text-sm font-bold text-gray-800">입력창을 <span className="text-yellow-600">꾹 눌러주세요</span></p>
                <p className="text-xs text-gray-500 leading-relaxed">메시지 입력창을 손가락으로 길게 누르면 메뉴가 나타납니다</p>
                <div className="mt-auto pt-2 flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-2 py-1.5">
                    <div className="flex-1 bg-white rounded px-2 py-1 border border-gray-200 text-xs text-gray-400">메시지 입력...</div>
                    <div className="w-5 h-5 flex items-center justify-center bg-yellow-400 rounded-full flex-shrink-0">
                      <i className="ri-send-plane-fill text-white" style={{ fontSize: '10px' }}></i>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-1.5 flex items-center gap-1.5">
                      <i className="ri-file-copy-line"></i>
                      <span className="font-bold">붙여넣기</span>
                      <span className="text-yellow-400 font-bold">← 누르세요!</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-2xl p-4 border border-green-200 flex flex-col gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full text-white font-extrabold text-sm flex-shrink-0">
                  <i className="ri-check-line text-sm"></i>
                </div>
                <p className="text-sm font-bold text-green-800">전송 버튼을 누르세요!</p>
                <p className="text-xs text-green-600 leading-relaxed">주문 내용이 자동으로 입력됩니다. 노란 전송 버튼을 누르면 주문 완료!</p>
                <div className="mt-auto pt-2 flex items-center gap-2 bg-white rounded-xl px-2 py-1.5 border border-green-100">
                  <div className="flex-1 bg-gray-50 rounded px-2 py-1 border border-gray-200 text-xs text-gray-700 truncate">[주문 정보] 이름: 홍길동...</div>
                  <div className="w-5 h-5 flex items-center justify-center bg-yellow-400 rounded-full flex-shrink-0">
                    <i className="ri-send-plane-fill text-white" style={{ fontSize: '10px' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ContactForm;
